// Google Places API (New) REST service
import { getConfig } from '../config'

export interface PlacePrediction {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  types: string[];
}

export interface PlaceDetails {
  place_id: string;
  formatted_address: string;
  address_components: Array<{
    long_name: string;
    short_name: string;
    types: string[];
  }>;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
  name: string;
}

export interface PlacesAutocompleteResponse {
  suggestions?: any[];
  predictions?: PlacePrediction[];
  status?: string;
}

export interface PlaceDetailsResponse {
  result: PlaceDetails;
  status: string;
}

class PlacesApiService {
  private apiKey: string | null = null;
  private baseUrl = 'https://places.googleapis.com/v1';

  /**
   * Get API key from runtime config
   */
  private getApiKey(): string {
    if (this.apiKey === null) {
      try {
        const config = getConfig();
        this.apiKey = config.googleMapsApiKey || '';
      } catch (error) {
        // Config not loaded yet, return empty string
        this.apiKey = '';
      }
    }
    return this.apiKey;
  }

  /**
   * Search for place predictions using Places API (New)
   */
  async searchPlaces(input: string, countryRestrictions: string[] = ['us', 'ca', 'gb', 'au']): Promise<PlacePrediction[]> {
    const apiKey = this.getApiKey();
    if (!apiKey) {
      throw new Error('Google Maps API key is not configured');
    }

    if (!input.trim()) {
      return [];
    }

    try {
      const response = await fetch(`${this.baseUrl}/places:autocomplete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': apiKey,
        },
        body: JSON.stringify({
          input,
          included_region_codes: countryRestrictions,
          language_code: 'en',
          region_code: 'us',
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        // console.error('❌ Places API HTTP Error:', response.status, response.statusText);
        // console.error('❌ Error response body:', errorText);
        throw new Error(`Places API HTTP error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      // console.log('🔍 Places API Response:', data);
      
      // Handle the actual response format from Places API (New)
      if (data.suggestions && Array.isArray(data.suggestions)) {
        // console.log('🔍 First suggestion raw:', JSON.stringify(data.suggestions[0], null, 2));
        
        // Convert suggestions to predictions format
        const predictions = data.suggestions.map((suggestion: any) => {
          // The actual data is nested inside placePrediction
          const pred = suggestion.placePrediction || suggestion;
          
          // Extract text from the prediction
          const mainText = pred.structuredFormat?.mainText?.text || 
                          pred.structuredFormat?.mainText ||
                          pred.text?.text ||
                          pred.text ||
                          '';
          
          const secondaryText = pred.structuredFormat?.secondaryText?.text ||
                               pred.structuredFormat?.secondaryText ||
                               '';
          
          const fullText = mainText + (secondaryText ? ', ' + secondaryText : '');
          
          // Extract place_id - it should be in pred.place or pred.placeId
          const placeId = pred.place || pred.placeId || pred.id || '';
          // console.log('🔍 Extracted place_id:', placeId, 'from pred:', pred);
          
          return {
            place_id: placeId,
            description: fullText,
            structured_formatting: {
              main_text: mainText,
              secondary_text: secondaryText,
            },
            types: pred.types || [],
          };
        });
        
        // console.log('🔍 Converted first prediction:', JSON.stringify(predictions[0], null, 2));
        return predictions;
      }

      // Fallback for old format
      if (data.predictions && Array.isArray(data.predictions)) {
        return data.predictions;
      }

      // console.warn('⚠️ Unexpected API response format:', data);
      return [];
    } catch (error) {
      // console.error('❌ Error searching places:', error);
      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error(`Unknown error: ${JSON.stringify(error)}`);
      }
    }
  }

  /**
   * Get detailed information about a place using place_id
   */
  async getPlaceDetails(placeId: string): Promise<PlaceDetails> {
    const apiKey = this.getApiKey();
    if (!apiKey) {
      throw new Error('Google Maps API key is not configured');
    }

    // console.log('🔍 Getting place details for place_id:', placeId);

    try {
      // The place_id already includes "places/" prefix, so just append it directly to baseUrl
      const response = await fetch(`${this.baseUrl}/${placeId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': apiKey,
          'X-Goog-FieldMask': 'id,formattedAddress,addressComponents,location,displayName',
        },
      });

      // console.log('🔍 Place details response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        // console.error('❌ Place details error response:', errorText);
        throw new Error(`Places API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      // console.log('🔍 Place details response:', data);
      
      // Convert the new API format to our expected format
      return {
        place_id: data.id || placeId,
        formatted_address: data.formattedAddress || '',
        address_components: data.addressComponents || [],
        geometry: {
          location: data.location || { lat: 0, lng: 0 },
        },
        name: data.displayName?.text || data.displayName || '',
      };
    } catch (error) {
      // console.error('❌ Error getting place details:', error);
      throw error;
    }
  }
}

export const placesApiService = new PlacesApiService();
